import { getGenesysAgentCallbackAvailability } from 'api/supportAssistant'
import DatePickerInput from 'components/Common/Form/Input/DatePickerInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import InputLabelWrap from 'components/Common/Form/Input/InputLabelWrap'
import Select from 'components/Common/Form/Input/Select'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getBrowserTimeZone, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import moment from 'moment'
import type { ComponentProps } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { formatCallbackTimeLabel } from './RequestAgentCallbackUtils'

const TIME_ZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Hobart',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Perth',
  'Pacific/Auckland',
]

const NOW = moment()

const tz = getBrowserTimeZone()

interface Props {
  type: App.GenesysAgentCallbackType
  fixedTimezone?: string
  label?: string
}

interface CallbackTimeInterval {
  key: string
  start: string
}

function AgentCallbackRequestDateTimeInput({ type, fixedTimezone, label }: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const [date, setDate] = useState<string | undefined>(NOW.format(ISO_DATE_FORMAT))

  const [time, setTime] = useState<string>()
  const [timezone, setTimezone] = useState<string>(fixedTimezone ?? tz)
  const [filteredTimes, setFilteredTimes] = useState<Array<CallbackTimeInterval>>([])

  const handleDateChange = useCallback<NonNullable<ComponentProps<typeof DatePickerInput>['onDateChange']>>((_, isoDate) => {
    setDate(isoDate)
  }, [])

  const fetchAvailability = useCallback(async(timezone: string, date: string | undefined) => {
    return await getGenesysAgentCallbackAvailability({
      date,
      timezone,
      type,
    })
  }, [type])

  const getTimezoneLabel = useCallback(
    (timezone: string) => {
      switch (timezone) {
        case 'Australia/Sydney':
          return intl.formatMessage({
            id: 'ksjk-o_g4ZPcBJuGGKYZ',
            defaultMessage: 'Sydney',
          })
        case 'Australia/Melbourne':
          return intl.formatMessage({
            id: 'CxBNTkzArnJLyNWPYqFB',
            defaultMessage: 'Melbourne',
          })
        case 'Australia/Brisbane':
          return intl.formatMessage({
            id: 'mvTBAPeLiL0clxJPpELW',
            defaultMessage: 'Brisbane',
          })
        case 'Australia/Hobart':
          return intl.formatMessage({
            id: 'dVBN-4tfaN7vA8nlrmdA',
            defaultMessage: 'Hobart',
          })
        case 'Australia/Adelaide':
          return intl.formatMessage({
            id: 'BYBASpn3Ae5vX3KDVKg7',
            defaultMessage: 'Adelaide',
          })
        case 'Australia/Darwin':
          return intl.formatMessage({
            id: 'xd_abgQDj5BqxIb5PIwu',
            defaultMessage: 'Darwin',
          })
        case 'Australia/Perth':
          return intl.formatMessage({
            id: '5taher6OUL8E9Lc-3aR-',
            defaultMessage: 'Perth',
          })
        case 'Pacific/Auckland':
          return intl.formatMessage({
            id: 'rCrlhoMyxga6hUHWwEHS',
            defaultMessage: 'Auckland',
          })
        default:
          return timezone
      }
    },
    [intl],
  )

  useEffect(() => {
    const fetchAndSetIntervals = async() => {
      const availability = await fetchAvailability(timezone, date)
      const times = availability.data.times

      if (times.length === 0) {
        const tomorrowsDate = moment(NOW).add(1, 'day')
        setDate(tomorrowsDate.format(ISO_DATE_FORMAT))
      }

      setFilteredTimes(times)

      // We don't want to set the default time for these callback types
      if (type !== 'SUPPORT' && type !== 'SALES' && times.length > 0) {
        setTime(times[0].start)
      }
    }

    fetchAndSetIntervals()
  }, [date, fetchAvailability, timezone, type])

  const { minDate, maxDate } = useMemo(() => {
    // Genesys constraints only allow callbacks up to 30 days in the future
    const maxDate = new Date(NOW.year(), NOW.month(), NOW.date() + 30)

    const lastAvailableTime = filteredTimes.at(-1)?.key
    if (lastAvailableTime) {
      const [lastAvailableHour] = lastAvailableTime.split(':').map(Number)
      if (NOW.hour() >= lastAvailableHour) {
        const minDate = new Date(NOW.year(), NOW.month(), NOW.date() + 1)
        return { minDate, maxDate }
      }
    }

    const minDate = new Date(NOW.year(), NOW.month(), NOW.date())
    return { minDate, maxDate }
  }, [filteredTimes])

  const dateTimeLabel = label ?? intl.formatMessage({
    id: 'pQGDyHSjptWDQuqnIeHc',
    defaultMessage: 'Preferred callback date and time',
  })

  return <Group direction="vertical" gap={8} horizontalAlign="stretch">
    <InputLabelWrap
      label={dateTimeLabel}
      optionalIndicatorEnabled={false}
    >
      <Group direction="horizontal" gap={8} horizontalAlign="stretch">
        <DatePickerInput
          required
          dropdownPlacement="bottom"
          name="callbackDate"
          min={minDate}
          max={maxDate}
          value={date}
          onDateChange={handleDateChange}
          requiredErrorMessage={intl.formatMessage({
            id: 'XahJjVF263CJLoqu9Vtb',
            defaultMessage: 'Please select date',
          })}
        />
        <Select
          required
          invalidErrorMessage={intl.formatMessage({
            id: '7Dpoa8pGZID2Yz4uUmbd',
            defaultMessage: 'Please select time',
          })}
          name="callbackTime"
          autoComplete="off"
          value={time}
          onChange={(e) => setTime(e.target.value)}
        >
          {filteredTimes.map((interval) => (
            <option key={interval.start} value={interval.start}>
              {formatCallbackTimeLabel(interval.start, intlDateFormatter, interval.key)}
            </option>
          ))}
        </Select>
      </Group>
      {fixedTimezone && <HiddenInput name="timezone" value={fixedTimezone} />}
      {!fixedTimezone && <Select
        required
        label={intl.formatMessage({
          id: 'O_7lHb4bXMYrP04Ij2tp',
          defaultMessage: 'Timezone',
        })}
        invalidErrorMessage={intl.formatMessage({
          id: 'cYsyXq3aRIxcnPXIy8OS',
          defaultMessage: 'Please select timezone',
        })}
        name="timezone"
        autoComplete="off"
        value={timezone}
        onChange={(e) => setTimezone(e.target.value)}
      >
        {TIME_ZONES.map((timezone) => (
          <option key={timezone} value={timezone}>
            {getTimezoneLabel(timezone)}
          </option>
        ))}
      </Select>}
    </InputLabelWrap>
  </Group>
}

export default AgentCallbackRequestDateTimeInput
